import { z } from 'zod'
import { errorContainsInvalidItems, errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import { buildM2MMutations, buildM2MLocalDBquery } from '@/lib/utils/m2mHelper'
import type {
  Coassign_Volumed_Assign_Insert_Input,
  Coassign_Volumed_Assign_Set_Input
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick, filterPick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist, isExistAll } from '@/lib/relation-graph'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { isWithinAvailableSelects } from '@/lib/zod/validators'

export const volumedAssignTableSchema = z.object({
  id: z.string().max(21),
  project_id: z.string().max(21).refine(isExist('project'), errorParentIsRemoved),
  job_title_id: z.string().max(21).refine(isExist('job_title'), errorParentIsRemoved),
  visible: z.boolean().default(true),
  comment: z.string().max(5000).nullable().default(null),
  icon: z.string().max(20).nullable().default(null)
})
const volumedAssignTableColumns = Object.keys(volumedAssignTableSchema.shape) as Array<
  keyof typeof volumedAssignTableSchema.shape
>

const volumedAssignRelationSchema = z.object({
  departmentIds: z
    .array(z.string().max(21))
    .default([])
    .refine(isWithinAvailableSelects(10), { message: '10個以下にしてください' })
    .refine(isExistAll('department'), errorContainsInvalidItems),
  jobSkillIds: z
    .array(z.string().max(21))
    .default([])
    .refine(isWithinAvailableSelects(10), { message: '10個以下にしてください' })
    .refine(isExistAll('job_skill'), errorContainsInvalidItems)
})

export const volumedAssignSchema = volumedAssignTableSchema.extend(
  volumedAssignRelationSchema.shape
)

export type VolumedAssign = z.infer<typeof volumedAssignSchema>

export class VolumedAssignMutator extends Mutator<typeof volumedAssignSchema> {
  static readonly stageableClassName = 'VolumedAssignMutator'
  schema = volumedAssignSchema
  table = 'volumed_assign' as const

  static async load(id: string, project_id: string): Promise<Reactive<VolumedAssignMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    // If project_id is not provided, use the one from base
    const projectId = project_id ?? base?.project_id ?? ''

    self.context = { id, project_id: projectId, visible: true }
    if (base) {
      self.base = {
        ...base,
        ...{ visible: Boolean(base.visible) }
      }
    }
    const departments = await this.getDepartmentIds(id)
    if (self.base) self.base.departmentIds = departments.map((record) => record.department_id)
    const jobSkills = await this.getJobskillIds(id)
    if (self.base) self.base.jobSkillIds = jobSkills.map((record) => record.job_skill_id)
    return self
  }

  private static async getDepartmentIds(id: string): Promise<{ department_id: string }[]> {
    return await withStage(async (db: Kysely<DB>) =>
      db
        .selectFrom('volumed_assign_to_department')
        .select(['department_id'])
        .where('volumed_assign_id', '=', id)
        .execute()
    )
  }

  private static async getJobskillIds(id: string): Promise<{ job_skill_id: string }[]> {
    return await withStage(async (db: Kysely<DB>) =>
      db
        .selectFrom('volumed_assign_to_job_skill')
        .select(['job_skill_id'])
        .where('volumed_assign_id', '=', id)
        .execute()
    )
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const id = this.context.id!
    const queries: InsertQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = {
      ...filterPick(rawData, ...volumedAssignTableColumns),
      ...{ visible: Number(rawData.visible) },
      ...{ tenant_id }
    }
    queries.push(DataBase.insertInto(this.table).values(sqliteParams).compile())
    const beforeJobSkillIds = this.base?.jobSkillIds || []
    const afterJobSkillIds = rawData.jobSkillIds
    queries.push(
      ...this.createVolumedAssignToJobSkillLocalDBQuery(
        id,
        beforeJobSkillIds,
        afterJobSkillIds,
        tenant_id
      )
    )

    // 部門関連のクエリを追加
    const beforeDepartmentIds = this.base?.departmentIds || []
    const afterDepartmentIds = rawData.departmentIds
    queries.push(
      ...this.createVolumedAssignToDepartmentLocalDBQuery(
        id,
        beforeDepartmentIds,
        afterDepartmentIds,
        tenant_id
      )
    )

    return queries
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const queries: UpdateQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = Object.fromEntries(
      Object.entries(filterPick(params, ...volumedAssignTableColumns)).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? Number(value) : value
      ])
    )
    if (Object.keys(sqliteParams).length > 0) {
      queries.push(
        DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()
      )
    }
    const beforeJobSkillIds = this.base?.jobSkillIds || []
    const afterJobSkillIds = rawData.jobSkillIds
    queries.push(
      ...this.createVolumedAssignToJobSkillLocalDBQuery(
        id,
        beforeJobSkillIds,
        afterJobSkillIds,
        tenant_id
      )
    )

    // 部門関連のクエリを追加
    const beforeDepartmentIds = this.base?.departmentIds || []
    const afterDepartmentIds = rawData.departmentIds
    queries.push(
      ...this.createVolumedAssignToDepartmentLocalDBQuery(
        id,
        beforeDepartmentIds,
        afterDepartmentIds,
        tenant_id
      )
    )

    return queries
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    const volumedAssignPlanDelete = DataBase.deleteFrom('volumed_assign_plan')
      .where('volumed_assign_id', '=', id)
      .compile()
    const volumedAssignToJobSkillDelete = DataBase.deleteFrom('volumed_assign_to_job_skill')
      .where('volumed_assign_id', '=', id)
      .compile()
    const volumedAssignToDepartmentDelete = DataBase.deleteFrom('volumed_assign_to_department')
      .where('volumed_assign_id', '=', id)
      .compile()
    return [
      volumedAssignPlanDelete,
      volumedAssignToJobSkillDelete,
      volumedAssignToDepartmentDelete,
      DataBase.deleteFrom(this.table).where('id', '=', id).compile()
    ]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const mutations: MutationOperationAndVariables[] = []
    const rawData = { ...(await this.parsed)! }
    const volumedAssignParams = { ...filterPick(rawData, ...volumedAssignTableColumns) }
    const variables: Coassign_Volumed_Assign_Insert_Input = {
      ...volumedAssignParams,
      ...{ commit_id }
    }
    mutations.push({
      mutation: insertVolumedAssignMutationDocument,
      variables: { input: variables }
    })
    mutations.push(
      ...this.createVolumedAssignToJobSkillMutations(
        id,
        this.base?.jobSkillIds || [],
        rawData.jobSkillIds,
        commit_id
      )
    )

    // 部門関連のミューテーションを追加
    mutations.push(
      ...this.createVolumedAssignToDepartmentMutations(
        id,
        this.base?.departmentIds || [],
        rawData.departmentIds,
        commit_id
      )
    )

    return mutations
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const mutations: MutationOperationAndVariables[] = []
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const volumedAssignParams = { ...filterPick(params, ...volumedAssignTableColumns) }
    const variables: Coassign_Volumed_Assign_Set_Input = {
      ...volumedAssignParams,
      ...{ commit_id }
    }
    if (Object.keys(variables).length > 0) {
      mutations.push({
        mutation: updateVolumedAssignMutationDocument,
        variables: {
          id: id,
          input: variables
        }
      })
    }
    mutations.push(
      ...this.createVolumedAssignToJobSkillMutations(
        id,
        this.base?.jobSkillIds || [],
        rawData.jobSkillIds,
        commit_id
      )
    )

    // 部門関連のミューテーションを追加
    mutations.push(
      ...this.createVolumedAssignToDepartmentMutations(
        id,
        this.base?.departmentIds || [],
        rawData.departmentIds,
        commit_id
      )
    )

    return mutations
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const deleted_at = formatISO(new Date())
    return [
      {
        mutation: updateDeletedAtVolumedAssignPlanMutationDocument,
        variables: { volumedAssignId: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtVolumedAssignToJobSkillMutationDocument,
        variables: { volumedAssignId: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtVolumedAssignToDepartmentMutationDocument,
        variables: { volumedAssignId: id, deletedAt: deleted_at, commit_id: commitId }
      },
      {
        mutation: updateDeletedAtVolumedAssignMutationDocument,
        variables: { id: id, deletedAt: deleted_at, commit_id: commitId }
      }
    ]
  }

  private createVolumedAssignToJobSkillMutations(
    volumedAssignId: string,
    beforeJobSkillIds: string[],
    afterJobSkillIds: string[],
    commitId: string
  ): MutationOperationAndVariables[] {
    return buildM2MMutations({
      fromColumn: 'volumed_assign_id',
      fromId: volumedAssignId,
      toColumn: 'job_skill_id',
      beforeToIds: beforeJobSkillIds,
      afterToIds: afterJobSkillIds,
      insertQuery: insertVolumedAssignToJobSkillMutationDocument,
      updateQuery: updateVolumedAssignToJobSkillMutationDocument,
      commitId: commitId
    })
  }

  private createVolumedAssignToJobSkillLocalDBQuery(
    volumedAssignId: string,
    beforeJobSkillIds: string[],
    afterJobSkillIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'volumed_assign_to_job_skill',
      fromColumn: 'volumed_assign_id',
      fromId: volumedAssignId,
      toColumn: 'job_skill_id',
      beforeToIds: beforeJobSkillIds,
      afterToIds: afterJobSkillIds,
      tenantId: tenantId
    })
  }

  private createVolumedAssignToDepartmentMutations(
    volumedAssignId: string,
    beforeDepartmentIds: string[],
    afterDepartmentIds: string[],
    commitId: string
  ): MutationOperationAndVariables[] {
    return buildM2MMutations({
      fromColumn: 'volumed_assign_id',
      fromId: volumedAssignId,
      toColumn: 'department_id',
      beforeToIds: beforeDepartmentIds,
      afterToIds: afterDepartmentIds,
      insertQuery: insertVolumedAssignToDepartmentMutationDocument,
      updateQuery: updateVolumedAssignToDepartmentMutationDocument,
      commitId: commitId
    })
  }

  private createVolumedAssignToDepartmentLocalDBQuery(
    volumedAssignId: string,
    beforeDepartmentIds: string[],
    afterDepartmentIds: string[],
    tenantId: string
  ) {
    return buildM2MLocalDBquery({
      mappingTable: 'volumed_assign_to_department',
      fromColumn: 'volumed_assign_id',
      fromId: volumedAssignId,
      toColumn: 'department_id',
      beforeToIds: beforeDepartmentIds,
      afterToIds: afterDepartmentIds,
      tenantId: tenantId
    })
  }
}

// 登録系
const insertVolumedAssignMutationDocument = graphql(/* GraphQL */ `
  mutation insertVolumedAssign($input: coassign_volumed_assign_insert_input!) {
    insert_coassign_volumed_assign_one(object: $input) {
      created_at
    }
  }
`)

const insertVolumedAssignToJobSkillMutationDocument = graphql(/* GraphQL */ `
  mutation insertVolumedAssignToJobSkillMutationDocument(
    $input: [coassign_volumed_assign_to_job_skill_insert_input!]!
  ) {
    insert_coassign_volumed_assign_to_job_skill(objects: $input) {
      affected_rows
    }
  }
`)

// 更新系
const updateVolumedAssignMutationDocument = graphql(/* GraphQL */ `
  mutation updateVolumedAssign($id: String!, $input: coassign_volumed_assign_set_input!) {
    update_coassign_volumed_assign_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)

const updateVolumedAssignToJobSkillMutationDocument = graphql(/* GraphQL */ `
  mutation updatVolumedAssignToJobSkillMutationDocument(
    $volumed_assign_id: String!
    $job_skill_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_to_job_skill(
      where: {
        volumed_assign_id: { _eq: $volumed_assign_id }
        job_skill_id: { _eq: $job_skill_id }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

// 削除系
const updateDeletedAtVolumedAssignMutationDocument = graphql(/* GraphQL */ `
  mutation updateVolumedAssignMutationDocument(
    $id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign(
      where: { id: { _eq: $id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtVolumedAssignPlanMutationDocument = graphql(/* GraphQL */ `
  mutation updateVolumedAssignPlanMutationDocument(
    $volumedAssignId: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_plan(
      where: { volumed_assign_id: { _eq: $volumedAssignId }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtVolumedAssignToJobSkillMutationDocument = graphql(/* GraphQL */ `
  mutation updateVolumedAssignToJobSkillMutationDocument(
    $volumedAssignId: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_to_job_skill(
      where: { volumed_assign_id: { _eq: $volumedAssignId }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const insertVolumedAssignToDepartmentMutationDocument = graphql(/* GraphQL */ `
  mutation insertVolumedAssignToDepartmentMutationDocument(
    $input: [coassign_volumed_assign_to_department_insert_input!]!
  ) {
    insert_coassign_volumed_assign_to_department(objects: $input) {
      affected_rows
    }
  }
`)

const updateVolumedAssignToDepartmentMutationDocument = graphql(/* GraphQL */ `
  mutation updateVolumedAssignToDepartmentMutationDocument(
    $volumed_assign_id: String!
    $department_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_to_department(
      where: {
        volumed_assign_id: { _eq: $volumed_assign_id }
        department_id: { _eq: $department_id }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)

const updateDeletedAtVolumedAssignToDepartmentMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtVolumedAssignToDepartmentMutationDocument(
    $volumedAssignId: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_volumed_assign_to_department(
      where: { volumed_assign_id: { _eq: $volumedAssignId }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)
